import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks/useDebounce';
import { getPublicProviderRoute } from '@/lib/routes';
import { supabase } from '@/integrations/supabase/client';
import { ChevronDown, Filter, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { logSearch } from '@/lib/searchLog';
import { nomePublico } from '@/lib/nomePublico';

interface SearchResult {
  id: string;
  type: 'categoria' | 'subcategoria' | 'servico' | 'prestador';
  title: string;
  description?: string;
  image?: string;
  count?: number;
  route: string;
  relevance: number;
}

interface UnifiedSearchBarProps {
  placeholder?: string;
  className?: string;
  onSearch?: (query: string) => void;
  showFilters?: boolean;
  variant?: 'default' | 'compact' | 'expanded';
}

export const UnifiedSearchBar: React.FC<UnifiedSearchBarProps> = ({
  placeholder = "Pesquisar categorias, serviços, prestadores...",
  className = "",
  onSearch,
  showFilters = true,
  variant = 'default'
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(new Set(['categoria', 'subcategoria', 'servico', 'prestador']));
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(query, 300);
  // Guarda o termo do pedido mais recente disparado. Sem isto, se o
  // utilizador pausar a meio de escrever (ex: "elet" ... "eletri"), os dois
  // pedidos ficam em voo e o que responder por último "ganha" — mesmo que
  // seja o mais antigo e já não corresponda ao que está na caixa.
  const latestQueryRef = useRef('');
  // Só se regista a pesquisa "assentada" (texto estável 1,5 s) ou a que levou
  // a um clique — nunca cada tecla, para não gravar "elet", "eletri", ...
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLoggedRef = useRef('');

  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);

  // Filtros disponíveis
  const availableFilters = [
    { key: 'categoria', label: 'Categorias', icon: '🏷️' },
    { key: 'subcategoria', label: 'Subcategorias', icon: '📂' },
    { key: 'servico', label: 'Serviços', icon: '🔧' },
    { key: 'prestador', label: 'Prestadores', icon: '👤' }
  ];

  // Fallback (pré-migração): pesquisa antiga por ILIKE em 4 tabelas. Sensível
  // a acentos — usado apenas se o RPC search_catalog ainda não existir.
  const legacyIlikeSearch = useCallback(async (searchQuery: string) => {
    const searchTerm = searchQuery.toLowerCase();
    const allResults: SearchResult[] = [];
    if (selectedFilters.has('categoria')) {
      const { data } = await supabase.from('categorias')
        .select('id, nome, descricao, icone_url').eq('ativo', true)
        .or(`nome.ilike.%${searchTerm}%,descricao.ilike.%${searchTerm}%`);
      (data || []).forEach((c: any) => allResults.push({ id: c.id, type: 'categoria', title: c.nome, description: c.descricao, image: c.icone_url, route: `/categorias/${c.id}`, relevance: 10 }));
    }
    if (selectedFilters.has('subcategoria')) {
      const { data } = await supabase.from('subcategorias')
        .select('id, nome, descricao, icone_url, categoria_id').eq('ativo', true)
        .or(`nome.ilike.%${searchTerm}%,descricao.ilike.%${searchTerm}%`);
      (data || []).forEach((s: any) => allResults.push({ id: s.id, type: 'subcategoria', title: s.nome, description: s.descricao, image: s.icone_url, route: `/categorias/${s.categoria_id}/${s.id}`, relevance: 9 }));
    }
    if (selectedFilters.has('servico')) {
      const { data } = await supabase.from('servicos')
        .select('id, titulo, descricao, icone_url, categoria_id, subcategoria_id').eq('ativo', true)
        .or(`titulo.ilike.%${searchTerm}%,descricao.ilike.%${searchTerm}%`);
      (data || []).forEach((sv: any) => allResults.push({ id: sv.id, type: 'servico', title: sv.titulo, description: sv.descricao, image: sv.icone_url, route: sv.subcategoria_id ? `/categorias/${sv.categoria_id}/${sv.subcategoria_id}` : `/categorias/${sv.categoria_id}`, relevance: 8 }));
    }
    if (selectedFilters.has('prestador')) {
      const { data } = await supabase.from('prestadores')
        .select('id, descricao, full_name, avatar_url').eq('is_active', true)
        .or(`descricao.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
      (data || []).forEach((p: any) => allResults.push({ id: p.id, type: 'prestador', title: nomePublico(p.full_name), description: p.descricao, image: p.avatar_url, route: getPublicProviderRoute(p.id), relevance: 7 }));
    }
    if (latestQueryRef.current === searchQuery) {
      setResults(allResults.slice(0, 20));
    }
  }, [selectedFilters]);

  // Função de busca unificada — usa o RPC `search_catalog`, que faz a pesquisa
  // com unaccent() no Postgres (insensível a acentos: "imoveis" encontra
  // "imóveis") sobre categorias/subcategorias/serviços/prestadores numa só
  // chamada, já ordenada por relevância. Substitui 4 queries ILIKE
  // (que eram sensíveis a acentos).
  const performUnifiedSearch = useCallback(async (searchQuery: string) => {
    latestQueryRef.current = searchQuery;

    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase.rpc('search_catalog', {
        p_term: searchQuery,
        p_types: Array.from(selectedFilters),
      });
      // Fallback: se o RPC ainda não estiver aplicado na BD (migração
      // 20260720_search_catalog_unaccent.sql), usar a pesquisa antiga por
      // ILIKE para não deixar a barra sem resultados.
      if (error) {
        console.warn('search_catalog indisponível, a usar fallback ILIKE:', error.message);
        await legacyIlikeSearch(searchQuery);
        return;
      }

      // Ignora respostas de pedidos já ultrapassados (ver latestQueryRef) —
      // sem isto, um pedido mais antigo que demore mais a responder podia
      // sobrepor-se ao resultado do termo atual na caixa.
      if (latestQueryRef.current !== searchQuery) return;

      const mapped: SearchResult[] = (data || []).map((row: any) => {
        let route = '#';
        if (row.type === 'categoria') {
          route = `/categorias/${row.categoria_id}`;
        } else if (row.type === 'subcategoria') {
          route = `/categorias/${row.categoria_id}/${row.subcategoria_id}`;
        } else if (row.type === 'servico') {
          route = row.subcategoria_id
            ? `/categorias/${row.categoria_id}/${row.subcategoria_id}`
            : `/categorias/${row.categoria_id}`;
        } else if (row.type === 'prestador') {
          route = getPublicProviderRoute(row.id);
        }
        return {
          id: row.id,
          type: row.type,
          title: row.title || (row.type === 'prestador' ? 'Prestador' : ''),
          description: row.description || undefined,
          image: row.image || undefined,
          route,
          relevance: row.relevance ?? 0,
        };
      });

      setResults(mapped);

      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => {
        if (latestQueryRef.current === searchQuery && lastLoggedRef.current !== searchQuery) {
          lastLoggedRef.current = searchQuery;
          logSearch(searchQuery, mapped.length);
        }
      }, 1500);
    } catch (error) {
      console.error('Erro na busca unificada:', error);
      if (latestQueryRef.current === searchQuery) setResults([]);
    } finally {
      if (latestQueryRef.current === searchQuery) setIsSearching(false);
    }
  }, [selectedFilters, legacyIlikeSearch]);

  // Efeito para executar busca quando query mudar
  useEffect(() => {
    if (debouncedQuery) {
      performUnifiedSearch(debouncedQuery);
    } else {
      setResults([]);
    }
  }, [debouncedQuery, performUnifiedSearch]);

  // Navegar para resultado
  const handleResultClick = (result: SearchResult) => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    lastLoggedRef.current = query.trim();
    logSearch(query, results.length, result.type);
    navigate(result.route);
    setShowResults(false);
    setQuery('');
    onSearch?.(result.title);
  };

  // Limpar busca
  const clearSearch = () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    setQuery('');
    setResults([]);
    setShowResults(false);
    onSearch?.('');
  };

  // Alternar filtro
  const toggleFilter = (filterKey: string) => {
    const newFilters = new Set(selectedFilters);
    if (newFilters.has(filterKey)) {
      newFilters.delete(filterKey);
    } else {
      newFilters.add(filterKey);
    }
    setSelectedFilters(newFilters);
  };

  // Renderizar ícone do tipo
  const getTypeIcon = (type: string) => {
    const icons = {
      categoria: '🏷️',
      subcategoria: '📂',
      servico: '🔧',
      prestador: '👤'
    };
    return icons[type as keyof typeof icons] || '📄';
  };

  // Renderizar badge do tipo
  const getTypeBadge = (type: string) => {
    const colors = {
      categoria: 'bg-blue-100 text-blue-800',
      subcategoria: 'bg-green-100 text-green-800',
      servico: 'bg-orange-100 text-orange-800',
      prestador: 'bg-purple-100 text-purple-800'
    };
    
    const labels = {
      categoria: 'Categoria',
      subcategoria: 'Subcategoria',
      servico: 'Serviço',
      prestador: 'Prestador'
    };

    return (
      <Badge className={`text-xs ${colors[type as keyof typeof colors]}`}>
        {labels[type as keyof typeof labels]}
      </Badge>
    );
  };

  const isCompact = variant === 'compact';
  const isExpanded = variant === 'expanded';

  return (
    <div className={`relative ${className}`}>
      {/* Barra de busca */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setShowResults(true)}
          placeholder={placeholder}
          className={`
            w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-orange-500 focus:border-transparent
            transition-all duration-200
            ${isCompact ? 'py-2 text-sm' : 'py-3'}
            ${isExpanded ? 'text-lg' : ''}
          `}
        />
        
        {/* Botão de filtros */}
        {showFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="absolute inset-y-0 right-8 flex items-center px-2 hover:bg-gray-100 rounded-r-none border-l"
          >
            <Filter className="h-4 w-4 text-gray-500" />
            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
          </Button>
        )}
        
        {/* Botão limpar */}
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Filtros */}
      {showFilters && isFiltersOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
          <div className="flex flex-wrap gap-2">
            {availableFilters.map(filter => (
              <button
                key={filter.key}
                onClick={() => toggleFilter(filter.key)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium
                  transition-all duration-200
                  ${selectedFilters.has(filter.key)
                    ? 'bg-orange-100 text-orange-800 border border-orange-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }
                `}
              >
                <span>{filter.icon}</span>
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resultados da busca */}
      {showResults && (query || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {isSearching ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
              Pesquisando...
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    {/* Imagem */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                      {result.image ? (
                        <OptimizedImage
                          src={result.image}
                          alt={result.title}
                          className="w-full h-full object-cover"
                          width={48}
                          height={48}
                          responsive
                          responsiveSizes={[48, 64, 96]}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          {getTypeIcon(result.type)}
                        </div>
                      )}
                    </div>
                    
                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 truncate">
                          {result.title}
                        </h4>
                        {getTypeBadge(result.type)}
                      </div>
                      
                      {result.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {result.description}
                        </p>
                      )}
                      
                      {result.count !== undefined && (
                        <p className="text-xs text-gray-500 mt-1">
                          {result.count} {result.type === 'prestador' ? 'serviços' : 'prestadores'}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>Nenhum resultado encontrado para "{query}"</p>
              <p className="text-sm">Tente ajustar os termos de pesquisa</p>
              <Link to="/pedir" className="mt-3 inline-block text-sm font-medium text-blue-600 underline">
                Não encontrou? Peça o serviço e nós procuramos o prestador
              </Link>
            </div>
          ) : null}
        </div>
      )}

      {/* Overlay para fechar resultados */}
      {showResults && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowResults(false)}
        />
      )}
    </div>
  );
};
